'use client'

import Link from "next/link"
import { Calendar, Mail as MailIcon, MapPin, Phone } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { GetAppSection } from "@/components/pwa/get-app-section"

export default function ContactPage() {

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#d60500] via-[#b50400] to-[#8b0300] text-white py-20 md:py-28 mt-16 md:mt-20">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-6 tracking-wider">
              <Calendar className="w-4 h-4" />
              SCHEDULE CONSULTATION
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 font-heading">
              Private Consultation
            </h1>
            <p className="text-xl md:text-2xl font-light mb-4 text-red-50">
              For Serious Buyers, Sellers, and Investors
            </p>
            <p className="text-lg leading-relaxed text-red-50">
              Expert guidance for premium property transactions across Metro Manila
            </p>
          </div>
        </div>
      </section>

      {/* Main CTA */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-1 h-8 bg-[#d60500]"></div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-heading">Schedule a Private Consultation</h2>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-2">
              Expert guidance for your luxury property transaction
            </p>
            <p className="text-sm text-gray-500 italic">
              This is not a general inquiry form
            </p>
          </div>

          <div className="text-center">
            <Button
              asChild
              size="lg"
              className="bg-[#d60500] hover:bg-[#b50400] text-white h-16 px-10 rounded-xl text-xl font-semibold tracking-wider shadow-xl"
            >
              <Link href="/consultation">
                <Calendar className="w-6 h-6 mr-3" />
                SCHEDULE PRIVATE CONSULTATION
              </Link>
            </Button>
            <p className="text-gray-600 mt-6 max-w-2xl mx-auto text-sm">
              Consultations are handled personally by our senior advisory team
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-1 h-8 bg-[#d60500]"></div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-heading">Contact Information</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <Card className="border-2 border-gray-200">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <MailIcon className="w-6 h-6 text-[#d60500]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Email</h3>
                <a href="mailto:info@spire.com.ph" className="text-[#d60500] hover:text-[#b50400] font-medium">
                  info@spire.com.ph
                </a>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-6 h-6 text-[#d60500]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Location</h3>
                <p className="text-gray-700">BGC, Taguig City<br />Metro Manila</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-6 h-6 text-[#d60500]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">WhatsApp</h3>
                <a href="https://wa.me/639602535917" target="_blank" rel="noopener noreferrer" className="text-[#d60500] hover:text-[#b50400] font-medium">
                  +63 960 253 5917
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Contact Us Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-1 h-8 bg-[#d60500]"></div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-heading">How We Can Help</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Property Inquiries",
                description: "Questions about available properties and viewings"
              },
              {
                title: "Buying & Selling",
                description: "Professional assistance with property transactions"
              },
              {
                title: "Rental Services",
                description: "Help finding or leasing out properties"
              },
              {
                title: "Investment Advice",
                description: "Market insights and investment opportunities"
              }
            ].map((item, index) => (
              <Card key={index} className="border border-gray-200 hover:border-[#d60500] hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Office Hours */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-1 h-8 bg-[#d60500]"></div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-heading">Business Hours</h2>
          </div>
          <div className="bg-white rounded-2xl p-8 border-2 border-gray-200">
            <p className="text-lg text-gray-700 mb-4">
              <strong className="text-gray-900">Monday - Friday:</strong> 9:00 AM - 6:00 PM
            </p>
            <p className="text-lg text-gray-700 mb-4">
              <strong className="text-gray-900">Saturday:</strong> 10:00 AM - 4:00 PM
            </p>
            <p className="text-lg text-gray-700">
              <strong className="text-gray-900">Sunday:</strong> By Appointment
            </p>
            <p className="text-sm text-gray-600 mt-6 italic">
              We respond to inquiries within 24 hours during business days
            </p>
          </div>
        </div>
      </section>

      <GetAppSection />
    </div>
  )
}
